import React from "react";
import { useLang } from "@/i18n/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_aceite-granada/artifacts/d8ycwu18_Logo%20CSM.webp";

/**
 * Brand mark for Cortijo Santa María.
 *
 * Combines the official engraved mark (olive-wreath crest with the family
 * estate inside) and a serif wordmark. The crest is the user-supplied logo
 * image (transparent background).
 *
 * Props:
 *   tone   "dark" → wordmark in olive-900 (default, used over cream backgrounds)
 *          "light" → wordmark in cream-50 (used over olive/dark backgrounds)
 *   size   "sm" | "md" (default) — controls crest + wordmark scale
 *   showWordmark   true (default) | false — useful for tight footers / favicons
 */
export default function Logo({
  className = "",
  tone = "dark",
  size = "md",
  showWordmark = true,
}) {
  const { t } = useLang();
  const color = tone === "dark" ? "#242A1D" : "#F8F6F0";
  const accent = "#C4A25C";
  const crestSize = size === "sm" ? 28 : 40;

  return (
    <a
      href="/"
      data-testid="brand-logo"
      className={`inline-flex items-center gap-3 group ${className}`}
      aria-label="Cortijo Santa María"
    >
      <img
        src={LOGO_URL}
        alt=""
        width={crestSize}
        height={crestSize}
        className="shrink-0 select-none"
        style={{
          width: crestSize,
          height: crestSize,
          // The original asset is on a black background but with the wreath
          // letterboxed; mix-blend-multiply removes the surrounding black on
          // light backgrounds so the wreath floats on the cream stage.
          mixBlendMode: tone === "dark" ? "multiply" : "screen",
        }}
        draggable={false}
      />
      {showWordmark && (
        <span className="flex flex-col leading-tight">
          <span
            className={`font-serif tracking-tight ${
              size === "sm" ? "text-[14px]" : "text-[15px] sm:text-[17px]"
            }`}
            style={{ color }}
          >
            Cortijo Santa María
          </span>
          <span
            className="text-[9px] sm:text-[10px] uppercase tracking-[0.28em] font-medium mt-0.5"
            style={{ color: accent }}
          >
            {t("logo.tagline")}
          </span>
        </span>
      )}
    </a>
  );
}
